/**
 * Stats panel rendered as an overlay on the map. Pure presentational —
 * receives already-computed totals so the same numbers can be re-used by
 * the surrounding landing page via the `conny-map-stats` CustomEvent
 * emitted from `index.tsx`.
 */
import { formatNumber } from "../../../shared/numbers";
import { useLocale, t } from "../../../shared/i18n";
import { scopeTitle, type MapScope, type MapStats } from "./stats";

export interface Props {
  stats: MapStats;
  scope: MapScope;
}

function fmtEuroDe(n: number): string {
  return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(
    Math.round(n),
  );
}

/**
 * Stats panel uses a <details> wrapper so it stays out of the map's way: by
 * default it's a single-line pill (total savings + case count) and it expands
 * to the full grid only when tapped — on every breakpoint. Keeping the map the
 * priority, the panel never opens itself. See styles.css for the layout.
 */
export function MapStatsPanel({ stats, scope }: Props) {
  const locale = useLocale();
  const title = scopeTitle(scope);
  const hasSavings = stats.total > 0;
  return (
    <details className="cn-map-panel cn-map-stats">
      <summary className="cn-map-stats__summary" role="status">
        <span className="cn-map-stats__line">
          {hasSavings && (
            <>
              <span className="cn-map-stats__eur">
                {fmtEuroDe(stats.total)} €
              </span>
              <span className="cn-map-stats__per">
                {t("map.perMonthShort", {}, locale)}
              </span>
              <span className="cn-map-stats__dot" aria-hidden="true">
                ·
              </span>
            </>
          )}
          <span className="cn-map-stats__count">
            {formatNumber(stats.cases)} {t("map.popupCasesPlural", {}, locale)}
          </span>
        </span>
      </summary>
      <div className="cn-map-stats__body">
        <div className="cn-map-stats__title">
          {t(title.key, title.vars, locale)}
        </div>
        <div className="cn-map-stats__grid">
          <div>
            <div className="cn-map-stats__k">
              {t("map.avgPerCase", {}, locale)}
            </div>
            <div className="cn-map-stats__v cn-map-stats__v--primary">
              {stats.cws > 0 ? fmtEuroDe(stats.total / stats.cws) + " €" : "–"}
            </div>
            <div className="cn-map-stats__note">
              {t(
                "map.statusLine",
                {
                  closed: formatNumber(stats.closed),
                  open: formatNumber(stats.open),
                },
                locale,
              )}
            </div>
          </div>
        </div>
      </div>
    </details>
  );
}
